import {
  FormDrawer,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { ToolScope, ToolStatus } from '../types';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  scope: z.enum(['PUBLIC', 'APPLICATION', 'TENANT']),
  status: z.enum(['ACTIVE', 'DRAFT', 'DEPRECATED']),
});

export type ToolCreateFormValues = z.infer<typeof schema>;

const DEFAULT_VALUES: ToolCreateFormValues = {
  name: '',
  description: '',
  scope: 'APPLICATION',
  status: 'DRAFT',
};

interface ToolCreateDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onCreate: (data: ToolCreateFormValues) => void;
}

export function ToolCreateDrawer({
  open,
  onOpenChange,
  isPending,
  onCreate,
}: ToolCreateDrawerProps) {
  const form = useForm<ToolCreateFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: form.reset is stable (react-hook-form)
  useEffect(() => {
    if (open) form.reset(DEFAULT_VALUES);
  }, [open]);

  return (
    <FormDrawer
      open={open}
      onOpenChange={(o) => {
        if (!o) form.reset();
        onOpenChange(o);
      }}
      title="New Tool"
      onSubmit={form.handleSubmit(onCreate)}
      isPending={isPending}
      submitDisabled={!form.watch('name')?.trim()}
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="tool-name">
            Name *
          </label>
          <Input id="tool-name" placeholder="Tool name" {...form.register('name')} />
          {form.formState.errors.name && (
            <p className="mt-1 text-xs text-destructive">{form.formState.errors.name.message}</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="tool-description">
            Description
          </label>
          <Textarea
            id="tool-description"
            placeholder="Tool description"
            {...form.register('description')}
          />
        </div>
        <div>
          <p className="mb-1 block text-sm font-medium">Scope</p>
          <Controller
            control={form.control}
            name="scope"
            render={({ field }) => (
              <Select value={field.value} onValueChange={(v) => field.onChange(v as ToolScope)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="APPLICATION">APPLICATION</SelectItem>
                  <SelectItem value="PUBLIC">PUBLIC</SelectItem>
                  <SelectItem value="TENANT">TENANT</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div>
          <p className="mb-1 block text-sm font-medium">Status</p>
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <Select value={field.value} onValueChange={(v) => field.onChange(v as ToolStatus)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">DRAFT</SelectItem>
                  <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                  <SelectItem value="DEPRECATED">DEPRECATED</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>
    </FormDrawer>
  );
}
